import { useCallback, useEffect, useRef, useState } from 'react';
import { MicVAD } from '@ricky0123/vad-web';
import { ChuckExperience } from './components/ChuckExperience';
import { MouseTable } from './components/MouseTable';
import { PortraitFrame } from './components/PortraitFrame';
import { TrainCockpit } from './components/TrainCockpit';
import { CANNED_CLIPS, cannedClipUrl, type CannedClip } from './lib/canned-clips';
import { playOwl, playRaven, playRumble } from './lib/idle-cues';
import { cueSqueak } from './lib/mouse-audio';
import {
  createSession,
  deleteSession,
  fetchHealth,
  fetchVideoQueueStatus,
  submitSceneEvent,
  submitTurn,
  turnAudioUrl,
  turnVideoUrl,
  type CharacterId,
  type TrainSpeedAction,
} from './lib/api';
import { CHARACTERS, CHARACTER_ORDER } from './lib/characters';
import { claimPortraitIntro } from './lib/portrait-intro';
import {
  PHASE_LABEL,
  phaseAfterSpeechEnd,
  phaseAfterSpeechStart,
  type InteractionPhase,
} from './lib/interaction';
import { PcmStreamPlayer, type VoiceBands } from './lib/pcm-player';
import { canStreamVideo, openVideoStream } from './lib/stream-video';
import { encodePcm16Wav, VAD_SAMPLE_RATE } from './lib/wav';
import {
  applyTrainEvent,
  INITIAL_TRAIN_SCENE,
  journeyProgressForRouteTime,
  nextTrainEventAtTime,
  sceneStateForContext,
  TRAIN_EVENTS,
  type TrainEvent,
  type TrainSceneState,
} from './lib/train-scene';

const SPOKEN_CAPTIONS_KEY = 'mara-spoken-captions';

/** Idle clips wait this long (plus jitter) after the gallery goes quiet. */
const IDLE_CLIP_MIN_MS = 25_000;
const IDLE_CLIP_JITTER_MS = 25_000;
/** How long Mara stays out of frame during the leave-the-frame gag. */
const AWAY_HOLD_MS = 6_500;

/** A scene moment played alongside an idle clip, rendered as an overlay. */
type SceneEffect = 'butterfly' | 'explosion';

/** Phases in which the gallery counts as quiet enough for an idle clip. */
function isIdlePhase(phase: InteractionPhase): boolean {
  return phase === 'listening' || phase === 'muted';
}

declare global {
  interface Window {
    /** Test hook: play a specific idle clip immediately. */
    __maraIdleClip?: (id?: string) => void;
    /** Test/demo hook: advance to a named train event immediately. */
    __trainTriggerEvent?: (id: TrainEvent['id']) => void;
    /** Test/demo hook: use the same bounded speed-control function as Iver. */
    __trainSetSpeed?: (targetKph: number, transitionSeconds?: number) => void;
  }
}

function isAbort(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}

export function App() {
  const requestedExperience = new URLSearchParams(window.location.search).get('experience');
  const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const ccaasExperience = normalizedPath === '/chuck' || requestedExperience === 'ccaas';
  const trainExperience = !ccaasExperience && requestedExperience === 'train';
  const audioOnlyExperience = trainExperience;
  const [awake, setAwake] = useState(false);
  const [activeCharacter, setActiveCharacter] = useState<CharacterId>(
    ccaasExperience ? 'chuck' : trainExperience ? 'robot' : 'mara',
  );
  const [phase, setPhaseState] = useState<InteractionPhase>('dormant');
  const [talkLevel, setTalkLevel] = useState(0);
  const [voiceBands, setVoiceBands] = useState<VoiceBands>([0, 0, 0, 0, 0]);
  const [userCaption, setUserCaption] = useState('');
  const [portraitCaption, setPortraitCaption] = useState('');
  const [muted, setMuted] = useState(false);
  const [spokenCaptions, setSpokenCaptions] = useState(() => {
    try {
      return window.localStorage.getItem(SPOKEN_CAPTIONS_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [renderQueueAhead, setRenderQueueAhead] = useState<number | null>(null);
  const [sceneEffect, setSceneEffect] = useState<SceneEffect | null>(null);
  const [away, setAway] = useState(false);
  const [idleRound, setIdleRound] = useState(0);
  const [trainScene, setTrainScene] = useState<TrainSceneState>(INITIAL_TRAIN_SCENE);
  const [routeResetKey, setRouteResetKey] = useState(0);
  const [speedTransitionSeconds, setSpeedTransitionSeconds] = useState(5);

  const phaseRef = useRef<InteractionPhase>('dormant');
  const mutedRef = useRef(false);
  const endedRef = useRef(false);
  const sessionIdRef = useRef('');
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const vadRef = useRef<MicVAD | null>(null);
  const playerRef = useRef<PcmStreamPlayer | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const maxSpeechTimerRef = useRef<number | null>(null);
  const queuePollTimerRef = useRef<number | null>(null);
  const operationRef = useRef(0);
  const dittoRef = useRef(false);
  const idleBusyRef = useRef(false);
  const lastIdleClipRef = useRef('');
  const videoPhaseRef = useRef<'greeting' | 'speaking'>('speaking');
  const videoDoneRef = useRef<((completed: boolean) => void) | null>(null);
  const handleUtteranceRef = useRef<(audio: Float32Array) => void>(() => undefined);
  const trainSceneRef = useRef<TrainSceneState>(INITIAL_TRAIN_SCENE);
  const firedTrainEventsRef = useRef(new Set<TrainEvent['id']>());
  const lastTrainSpeedCommandAtRef = useRef(-Infinity);
  const greetedCharactersRef = useRef(new Set<CharacterId>());

  const setPhase = useCallback((next: InteractionPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const clearSpeechTimer = useCallback(() => {
    if (maxSpeechTimerRef.current !== null) window.clearTimeout(maxSpeechTimerRef.current);
    maxSpeechTimerRef.current = null;
  }, []);

  const stopQueuePolling = useCallback(() => {
    if (queuePollTimerRef.current !== null) window.clearTimeout(queuePollTimerRef.current);
    queuePollTimerRef.current = null;
    setRenderQueueAhead(null);
  }, []);

  const clearVideo = useCallback((completed: boolean) => {
    stopQueuePolling();
    const resolveDone = videoDoneRef.current;
    videoDoneRef.current = null;
    setVideoSrc((current) => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
      return null;
    });
    resolveDone?.(completed);
  }, [stopQueuePolling]);

  const setTrainSpeed = useCallback((targetKph: number, transitionSeconds = 5) => {
    if (!trainExperience || !Number.isFinite(targetKph)) return;
    const roundedTarget = Math.round(targetKph);
    const speed = roundedTarget <= 0 ? 0 : Math.max(25, Math.min(80, roundedTarget));
    const transition = Math.max(0, Math.min(12, transitionSeconds));
    const next = { ...trainSceneRef.current, trainSpeedKph: speed };
    trainSceneRef.current = next;
    setSpeedTransitionSeconds(transition);
    setTrainScene(next);
  }, [trainExperience]);

  const applyTurnAction = useCallback((action: TrainSpeedAction | null | undefined) => {
    if (!action || action.type !== 'set_train_speed') return;
    const now = performance.now();
    if (now - lastTrainSpeedCommandAtRef.current < 3_500) return;
    lastTrainSpeedCommandAtRef.current = now;
    setTrainSpeed(action.targetKph, action.transitionSeconds);
  }, [setTrainSpeed]);

  const showError = useCallback((error: unknown) => {
    const message = error instanceof Error ? error.message : 'The portrait could not awaken.';
    setErrorMessage(message);
    setPhase('error');
  }, [setPhase]);

  const releaseResources = useCallback(async (removeRemote: boolean) => {
    operationRef.current += 1;
    requestRef.current?.abort();
    requestRef.current = null;
    clearSpeechTimer();
    clearVideo(false);
    playerRef.current?.destroy();
    playerRef.current = null;
    try {
      vadRef.current?.destroy();
    } catch {
      // A partially initialized VAD has nothing useful left to release.
    }
    vadRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    const context = contextRef.current;
    contextRef.current = null;
    if (context && context.state !== 'closed') await context.close().catch(() => undefined);
    const sessionId = sessionIdRef.current;
    sessionIdRef.current = '';
    if (removeRemote && sessionId) await deleteSession(sessionId);
    setTalkLevel(0);
    setVoiceBands([0, 0, 0, 0, 0]);
  }, [clearSpeechTimer, clearVideo]);

  const playPortraitVideo = useCallback((
    sessionId: string,
    turnId: string,
    speechPhase: 'greeting' | 'speaking',
  ): Promise<boolean> => {
    if (!canStreamVideo()) {
      throw new Error('This browser cannot play the required portrait video stream.');
    }
    // MediaSource streams the fragmented MP4 into the video element while
    // later frames are still being painted server-side; onPlaying flips the
    // phase, onEnded/onError resolve the promise, aborting cancels the fetch.
    const controller = new AbortController();
    requestRef.current = controller;
    stopQueuePolling();
    setPhase('conjuring');
    const done = new Promise<boolean>((resolve) => {
      videoDoneRef.current = resolve;
    });
    videoPhaseRef.current = speechPhase;
    setVideoSrc(openVideoStream(turnVideoUrl(sessionId, turnId), controller.signal));
    const pollQueue = async () => {
      if (
        controller.signal.aborted
        || videoDoneRef.current === null
        || phaseRef.current !== 'conjuring'
      ) return;
      try {
        const status = await fetchVideoQueueStatus(sessionId, turnId, controller.signal);
        if (phaseRef.current !== 'conjuring') return;
        setRenderQueueAhead(status.state === 'queued' ? status.ahead : null);
      } catch {
        if (controller.signal.aborted) return;
      }
      if (phaseRef.current !== 'conjuring') return;
      queuePollTimerRef.current = window.setTimeout(() => { void pollQueue(); }, 500);
    };
    void pollQueue();
    return done;
  }, [setPhase, stopQueuePolling]);

  const handleVideoPlaying = useCallback(() => {
    stopQueuePolling();
    if (phaseRef.current === 'conjuring') setPhase(videoPhaseRef.current);
  }, [setPhase, stopQueuePolling]);

  const handleVideoError = useCallback(() => {
    clearVideo(false);
    showError(new Error('The required portrait video stream failed.'));
  }, [clearVideo, showError]);

  const speakTurn = useCallback(async (
    turnId: string,
    text: string,
    speechPhase: 'greeting' | 'speaking',
    operation: number,
  ) => {
    const sessionId = sessionIdRef.current;
    const player = playerRef.current;
    if (!sessionId || endedRef.current || operation !== operationRef.current) return;
    setPortraitCaption(text);
    if (audioOnlyExperience) {
      if (!player) throw new Error('The required speech player is unavailable.');
      setPhase(speechPhase);
      await player.play(turnAudioUrl(sessionId, turnId));
    } else {
      if (!dittoRef.current) throw new Error('The required portrait renderer is unavailable.');
      const completed = await playPortraitVideo(sessionId, turnId, speechPhase);
      if (operation !== operationRef.current || endedRef.current) return;
      if (!completed) {
        if (phaseRef.current !== 'error') showError(new Error('The required portrait video stream stopped.'));
        return;
      }
    }
    if (operation !== operationRef.current || endedRef.current) return;
    if (phaseRef.current === speechPhase) {
      setPhase(mutedRef.current ? 'muted' : 'listening');
      window.setTimeout(() => setPortraitCaption(''), 1600);
    }
  }, [audioOnlyExperience, playPortraitVideo, setPhase, showError]);

  const handleUtterance = useCallback(async (audio: Float32Array) => {
    if (endedRef.current || mutedRef.current || !sessionIdRef.current) return;
    const hasAudio = audio.length >= Math.floor(VAD_SAMPLE_RATE * 0.12);
    setPhase(phaseAfterSpeechEnd('recording', hasAudio));
    if (!hasAudio) return;
    const operation = ++operationRef.current;
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setUserCaption('');
    setPortraitCaption('');
    try {
      const turn = await submitTurn(
        sessionIdRef.current,
        encodePcm16Wav(audio),
        controller.signal,
        trainExperience ? sceneStateForContext(trainSceneRef.current) : undefined,
      );
      if (operation !== operationRef.current || endedRef.current) return;
      applyTurnAction(turn.action);
      setUserCaption(turn.transcript);
      setPortraitCaption(turn.reply);
      setPhase('thinking');
      await wait(520);
      if (operation !== operationRef.current || endedRef.current) return;
      await speakTurn(turn.turnId, turn.reply, 'speaking', operation);
    } catch (error) {
      if (!isAbort(error) && operation === operationRef.current && !endedRef.current) showError(error);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [applyTurnAction, setPhase, showError, speakTurn, trainExperience]);

  handleUtteranceRef.current = (audio) => void handleUtterance(audio);

  const playIdleClip = useCallback(async (requestedId?: string) => {
    if (idleBusyRef.current || endedRef.current || !isIdlePhase(phaseRef.current)) return;
    // A live turn increments the operation counter; capturing it here makes
    // any interruption (speech, character switch, End) cancel the clip.
    const operation = operationRef.current;
    idleBusyRef.current = true;
    try {
      const requested = requestedId
        ? CANNED_CLIPS.find((candidate) => candidate.id === requestedId)
        : undefined;
      const pool = CANNED_CLIPS.filter(
        (candidate) => candidate.random !== false && candidate.id !== lastIdleClipRef.current,
      );
      const clip: CannedClip = requested ?? pool[Math.floor(Math.random() * pool.length)];
      lastIdleClipRef.current = clip.id;

      // The cue leads the line, so the moment reads cause-then-reaction.
      const context = contextRef.current;
      if (clip.cue === 'owl' && context) {
        playOwl(context);
        await wait(1500);
      } else if (clip.cue === 'raven' && context) {
        playRaven(context);
        await wait(1100);
      } else if (clip.cue === 'squeak') {
        cueSqueak();
        await wait(900);
      } else if (clip.cue === 'butterfly') {
        setSceneEffect('butterfly');
        await wait(1800);
      } else if (clip.cue === 'explosion') {
        if (context) playRumble(context);
        setSceneEffect('explosion');
        await wait(1000);
      }
      if (operation !== operationRef.current || endedRef.current || !isIdlePhase(phaseRef.current)) return;

      setPortraitCaption(clip.text);
      const done = new Promise<boolean>((resolve) => {
        videoDoneRef.current = resolve;
      });
      setVideoSrc(cannedClipUrl(clip));
      const completed = await done;
      if (!completed) return;

      if (clip.cue === 'butterfly') await wait(2000);
      if (clip.exit && !endedRef.current) {
        setAway(true);
        await wait(AWAY_HOLD_MS);
        setAway(false);
        await wait(1500);
      }
      window.setTimeout(() => {
        setPortraitCaption((current) => (current === clip.text ? '' : current));
      }, 1200);
    } finally {
      idleBusyRef.current = false;
      setSceneEffect(null);
      setAway(false);
      setIdleRound((round) => round + 1);
    }
  }, []);

  const triggerTrainEvent = useCallback(async (event: TrainEvent) => {
    if (!trainExperience || firedTrainEventsRef.current.has(event.id)) return;
    firedTrainEventsRef.current.add(event.id);
    const nextScene = applyTrainEvent(trainSceneRef.current, event);
    trainSceneRef.current = nextScene;
    if (typeof event.patch.trainSpeedKph === 'number') setSpeedTransitionSeconds(5);
    setTrainScene(nextScene);

    // The world always advances, but a proactive line only starts while the
    // shared speech pipeline is idle, so it never talks over the co-pilot.
    if (!sessionIdRef.current || endedRef.current || phaseRef.current !== 'listening') return;
    const operation = ++operationRef.current;
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setUserCaption('');
    try {
      setPhase('thinking');
      const turn = await submitSceneEvent(
        sessionIdRef.current,
        event.announcement,
        sceneStateForContext(nextScene),
        controller.signal,
      );
      if (operation !== operationRef.current || endedRef.current) return;
      applyTurnAction(turn.action);
      setPortraitCaption(turn.reply);
      await wait(320);
      if (operation !== operationRef.current || endedRef.current) return;
      await speakTurn(turn.turnId, turn.reply, 'speaking', operation);
    } catch (error) {
      if (!isAbort(error) && operation === operationRef.current && !endedRef.current) showError(error);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [applyTurnAction, setPhase, showError, speakTurn, trainExperience]);

  const handleRouteTimeUpdate = useCallback((currentTime: number, duration: number) => {
    if (!trainExperience || !awake || endedRef.current) return;
    const progress = journeyProgressForRouteTime(currentTime, duration);
    if (Math.abs(progress - trainSceneRef.current.journeyProgress) > 0.1) {
      const next = { ...trainSceneRef.current, journeyProgress: progress };
      trainSceneRef.current = next;
      setTrainScene(next);
    }
    const due = nextTrainEventAtTime(currentTime, firedTrainEventsRef.current);
    if (due) void triggerTrainEvent(due);
  }, [awake, trainExperience, triggerTrainEvent]);

  useEffect(() => {
    if (!trainExperience) return;
    window.__trainTriggerEvent = (id) => {
      const event = TRAIN_EVENTS.find((candidate) => candidate.id === id);
      if (event) void triggerTrainEvent(event);
    };
    return () => { delete window.__trainTriggerEvent; };
  }, [trainExperience, triggerTrainEvent]);

  useEffect(() => {
    if (!trainExperience) return;
    window.__trainSetSpeed = setTrainSpeed;
    return () => { delete window.__trainSetSpeed; };
  }, [setTrainSpeed, trainExperience]);

  // While Mara's frame is quiet, occasionally play one of her canned moments.
  useEffect(() => {
    if (!awake || activeCharacter !== 'mara' || !isIdlePhase(phase) || videoSrc) return;
    const delay = IDLE_CLIP_MIN_MS + Math.random() * IDLE_CLIP_JITTER_MS;
    const timer = window.setTimeout(() => void playIdleClip(), delay);
    return () => window.clearTimeout(timer);
  }, [awake, activeCharacter, phase, videoSrc, idleRound, playIdleClip]);

  useEffect(() => {
    window.__maraIdleClip = (id?: string) => void playIdleClip(id);
    return () => {
      delete window.__maraIdleClip;
    };
  }, [playIdleClip]);

  const enterPortrait = useCallback(async (characterId: CharacterId = activeCharacter) => {
    endedRef.current = false;
    await releaseResources(true);
    setActiveCharacter(characterId);
    setAwake(true);
    setMuted(false);
    mutedRef.current = false;
    setHelpOpen(false);
    setErrorMessage('');
    setUserCaption('');
    setPortraitCaption('');
    if (characterId === 'robot') {
      trainSceneRef.current = INITIAL_TRAIN_SCENE;
      setTrainScene(INITIAL_TRAIN_SCENE);
      firedTrainEventsRef.current.clear();
      lastTrainSpeedCommandAtRef.current = -Infinity;
      setSpeedTransitionSeconds(5);
      setRouteResetKey((key) => key + 1);
    }
    setPhase('connecting');
    const operation = ++operationRef.current;
    try {
      const AudioContextClass = window.AudioContext;
      const context = new AudioContextClass({ latencyHint: 'interactive' });
      contextRef.current = context;
      await context.resume();
      const player = new PcmStreamPlayer(context, { onLevel: setTalkLevel, onBands: setVoiceBands });
      playerRef.current = player;
      await player.initialize();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      const vad = await MicVAD.new({
        model: 'v5',
        baseAssetPath: '/vendor/vad/',
        onnxWASMBasePath: '/vendor/vad/',
        getStream: async () => stream,
        pauseStream: async () => undefined,
        resumeStream: async () => stream,
        startOnLoad: false,
        preSpeechPadMs: 300,
        redemptionMs: 1200,
        minSpeechMs: 150,
        positiveSpeechThreshold: 0.5,
        negativeSpeechThreshold: 0.35,
        submitUserSpeechOnPause: true,
        onSpeechStart: () => {
          if (endedRef.current || mutedRef.current) return;
          operationRef.current += 1;
          requestRef.current?.abort();
          playerRef.current?.stop();
          clearVideo(false);
          setUserCaption('');
          setPortraitCaption('');
          setPhase(phaseAfterSpeechStart(phaseRef.current, mutedRef.current));
          clearSpeechTimer();
          maxSpeechTimerRef.current = window.setTimeout(() => {
            const activeVad = vadRef.current;
            if (!activeVad || endedRef.current) return;
            void activeVad.pause().then(() => {
              if (!endedRef.current && !mutedRef.current) return activeVad.start();
              return undefined;
            });
          }, 20_000);
        },
        onSpeechRealStart: () => {
          if (!endedRef.current && !mutedRef.current) setPhase('recording');
        },
        onSpeechEnd: (audio) => {
          clearSpeechTimer();
          handleUtteranceRef.current(audio);
        },
        onVADMisfire: () => {
          clearSpeechTimer();
          if (phaseRef.current === 'recording') setPhase(mutedRef.current ? 'muted' : 'listening');
        },
      });
      vadRef.current = vad;
      const health = await fetchHealth();
      if (!health.groqConfigured || !health.stt || !health.higgs) {
        throw new Error('Required speech services are unavailable.');
      }
      if (characterId !== 'robot') {
        if (!health.ditto) throw new Error('The required portrait renderer is unavailable.');
        if (!canStreamVideo()) throw new Error('This browser cannot play the required portrait video stream.');
        dittoRef.current = true;
      } else {
        dittoRef.current = false;
      }
      const session = await createSession(characterId);
      if (operation !== operationRef.current || endedRef.current) return;
      sessionIdRef.current = session.sessionId;
      await vad.start();
      const shouldPlayIntro = characterId === 'robot' || characterId === 'chuck'
        || claimPortraitIntro(greetedCharactersRef.current, characterId);
      if (shouldPlayIntro) {
        await speakTurn(session.greetingTurnId, session.greeting, 'greeting', operation);
      } else {
        setPhase('listening');
      }
    } catch (error) {
      await releaseResources(true);
      if (!endedRef.current) showError(error);
    }
  }, [activeCharacter, clearSpeechTimer, clearVideo, releaseResources, setPhase, showError, speakTurn]);

  const toggleMute = useCallback(() => {
    if (endedRef.current) return;
    const next = !mutedRef.current;
    mutedRef.current = next;
    setMuted(next);
    streamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !next;
    });
    if (next) {
      clearSpeechTimer();
      setPhase('muted');
    } else {
      setPhase('listening');
    }
  }, [clearSpeechTimer, setPhase]);

  const toggleSpokenCaptions = useCallback(() => {
    setSpokenCaptions((visible) => {
      const next = !visible;
      try {
        window.localStorage.setItem(SPOKEN_CAPTIONS_KEY, next ? 'on' : 'off');
      } catch {
        // The setting still works for this page when storage is unavailable.
      }
      return next;
    });
  }, []);

  const endSession = useCallback(async () => {
    endedRef.current = true;
    greetedCharactersRef.current.clear();
    setPhase('ended');
    setUserCaption('');
    setPortraitCaption(
      ccaasExperience
        ? 'The communications car is standing by.'
        : trainExperience ? 'Northstar standing by.' : 'Until the frames stir again.',
    );
    await releaseResources(true);
  }, [ccaasExperience, releaseResources, setPhase, trainExperience]);

  useEffect(() => () => {
    endedRef.current = true;
    void releaseResources(true);
  }, [releaseResources]);

  const selectCharacter = useCallback((characterId: CharacterId) => {
    if (characterId === activeCharacter) return;
    if (!awake) {
      setActiveCharacter(characterId);
      return;
    }
    void enterPortrait(characterId);
  }, [activeCharacter, awake, enterPortrait]);

  const speaking = phase === 'greeting' || phase === 'speaking' || talkLevel > 0.025;
  const caption = phase === 'thinking' || phase === 'transcribing'
    ? userCaption
    : spokenCaptions ? portraitCaption : '';
  const micActive = awake && !muted && !['connecting', 'error', 'ended'].includes(phase);
  const activeProfile = CHARACTERS[activeCharacter];
  const experienceName = ccaasExperience ? 'ccaas' : trainExperience ? 'train' : 'portrait';
  const shellClass = ccaasExperience ? 'chuck-shell' : trainExperience ? 'train-shell' : 'portrait-shell';
  const phaseLabel = phase === 'conjuring' && renderQueueAhead !== null
    ? `Queued · ${renderQueueAhead} ahead`
    : phase === 'conjuring'
      ? `Painting ${activeCharacter === 'mara' ? 'her' : 'his'} reply`
      : PHASE_LABEL[phase];

  return (
    <main
      className={`app-shell ${shellClass} ${awake ? 'is-awake' : 'is-dormant'} phase-${phase} ${sceneEffect === 'explosion' ? 'is-quaking' : ''}`}
      data-phase={phase}
      data-experience={experienceName}
    >
      {!trainExperience && !ccaasExperience && (
        <>
          <div className="stone-vignette" aria-hidden="true" />
          <div className="wall-sconce wall-sconce-left" aria-hidden="true"><i /></div>
          <div className="wall-sconce wall-sconce-right" aria-hidden="true"><i /></div>
          <div className="room-floor" aria-hidden="true" />
          <div className="room-baseboard" aria-hidden="true" />
          <div className="room-wainscot" aria-hidden="true" />
          <figure
            className="northstar-portal"
            role="img"
            aria-label="A living window into the Northstar world at dusk, where a brass steam train crosses a snowy alpine viaduct toward the horizon"
          >
            <svg className="portal-cord" viewBox="0 0 100 14" preserveAspectRatio="none" aria-hidden="true"><path d="M2 14 L50 2 L98 14" /></svg>
            <span className="portal-nail" aria-hidden="true" />
            <div className="portal-frame" aria-hidden="true">
              <div className="portal-window">
                <img
                  className="portal-poster"
                  src="/assets/paintings/northstar-approach-start-v1.png"
                  alt=""
                />
                <span className="portal-glass" />
              </div>
            </div>
          </figure>
          <MouseTable />

          {sceneEffect === 'butterfly' && (
            <div className="butterfly-flight" aria-hidden="true">
              <div className="butterfly-bob">
                <svg className="butterfly" viewBox="0 0 24 20">
                  <path className="butterfly-wing" d="M11 10 C 6 2, 1 3, 2 8 C 2.6 11.4, 7 13, 11 11 Z" />
                  <path className="butterfly-wing" d="M13 10 C 18 2, 23 3, 22 8 C 21.4 11.4, 17 13, 13 11 Z" />
                  <path className="butterfly-wing butterfly-wing-low" d="M11 11.4 C 7.4 15, 4 17.4, 6 18.6 C 8 19.8, 11 15.8, 11.6 12.6 Z" />
                  <path className="butterfly-wing butterfly-wing-low" d="M13 11.4 C 16.6 15, 20 17.4, 18 18.6 C 16 19.8, 13 15.8, 12.4 12.6 Z" />
                  <ellipse className="butterfly-body" cx="12" cy="10.6" rx="1" ry="3.4" />
                </svg>
              </div>
            </div>
          )}
          {sceneEffect === 'explosion' && <div className="explosion-flash" aria-hidden="true" />}

          <div className="portrait-gallery" aria-label="The enchanted portrait gallery">
            {CHARACTER_ORDER.map((characterId) => {
              const character = CHARACTERS[characterId];
              const active = characterId === activeCharacter;
              return (
                <PortraitFrame
                  key={characterId}
                  character={character}
                  active={active}
                  awake={awake}
                  caption={active ? caption : ''}
                  captionIsUser={active && phase === 'thinking'}
                  away={active && away}
                  videoSrc={active ? videoSrc : null}
                  onSelect={() => selectCharacter(characterId)}
                  onVideoPlaying={handleVideoPlaying}
                  onVideoEnded={() => clearVideo(true)}
                  onVideoError={handleVideoError}
                />
              );
            })}
          </div>
        </>
      )}

      {ccaasExperience && (
        <ChuckExperience
          caption={caption}
          captionIsUser={phase === 'thinking' && Boolean(userCaption)}
          speaking={speaking}
          voiceBands={voiceBands}
          videoSrc={videoSrc}
          onVideoPlaying={handleVideoPlaying}
          onVideoEnded={() => clearVideo(true)}
          onVideoError={handleVideoError}
        />
      )}

      {trainExperience && (
        <TrainCockpit
          awake={awake}
          caption={caption}
          captionIsUser={phase === 'thinking' && Boolean(userCaption)}
          phase={phase}
          scene={trainScene}
          speaking={speaking}
          voiceBands={voiceBands}
          routePlaying={phase !== 'ended'}
          routeResetKey={routeResetKey}
          speedTransitionSeconds={speedTransitionSeconds}
          onRouteTimeUpdate={handleRouteTimeUpdate}
          onRouteError={() => showError(new Error('The required Northstar route video failed.'))}
        />
      )}

      {!awake && (
        <div className="entry-veil">
          <div className="entry-copy">
            <p className="eyebrow">
              {ccaasExperience
                ? 'Charles King Consulting · Communications Car'
                : trainExperience ? 'Northstar Alpine Line · 06:40' : 'The East Alchemy Stair'}
            </p>
            <h1>
              {ccaasExperience
                ? <>Contact centers.<br />Without the brochure voice.</>
                : trainExperience ? <>The mountains<br />are calling.</> : <>Some portraits<br />prefer to watch.</>}
            </h1>
            <button
              className="wake-button"
              onClick={() => void enterPortrait(
                ccaasExperience ? 'chuck' : trainExperience ? 'robot' : activeCharacter,
              )}
              type="button"
            >
              <span>
                {ccaasExperience
                  ? 'Enter the communications car'
                  : trainExperience ? 'Take the co-pilot chair' : 'Enter the gallery'}
              </span>
              <i aria-hidden="true">✦</i>
            </button>
            <p className="prototype-note">
              Your microphone is used only during this {ccaasExperience ? 'conversation' : trainExperience ? 'journey' : 'visit'}
            </p>
          </div>
        </div>
      )}

      {awake && phase !== 'ended' && (
        <div className="interaction-bar">
          <div className={`presence-status phase-indicator phase-${phase}`} role="status" aria-live="polite">
            <span className="status-orb" aria-hidden="true" />
            <span>{phaseLabel}</span>
          </div>
          <div className={`mic-privacy ${micActive ? 'is-active' : ''} ${muted ? 'is-muted' : ''}`}>
            <span className="mic-rings" aria-hidden="true"><i /><i /><i /></span>
            <span>{muted ? 'Mic off' : micActive ? 'Mic on' : 'Mic waiting'}</span>
          </div>
          <div className="control-buttons">
            <button type="button" onClick={toggleMute} aria-label={muted ? 'Unmute microphone' : 'Mute microphone'} aria-pressed={muted} disabled={phase === 'connecting' || phase === 'error'}>
              <span aria-hidden="true">{muted ? '◉' : '◌'}</span>{muted ? 'Unmute' : 'Mute'}
            </button>
            <button className={`caption-toggle ${spokenCaptions ? '' : 'is-off'}`} type="button" onClick={toggleSpokenCaptions} aria-label={spokenCaptions ? 'Hide spoken captions' : 'Show spoken captions'} aria-pressed={spokenCaptions}>
              <span aria-hidden="true">CC</span> {spokenCaptions ? 'On' : 'Off'}
            </button>
            <button type="button" onClick={() => setHelpOpen((open) => !open)} aria-expanded={helpOpen} aria-controls="portrait-help"><span aria-hidden="true">?</span> Help</button>
            <button className="end-button" type="button" onClick={() => void endSession()}><span aria-hidden="true">×</span> End</button>
          </div>
        </div>
      )}

      {helpOpen && phase !== 'ended' && (
        <aside
          className="help-card"
          id="portrait-help"
          aria-label={
            ccaasExperience
              ? 'How to explore the CCaaS offerings'
              : trainExperience ? 'How to join the journey' : 'How to speak with the portraits'
          }
        >
          <button type="button" onClick={() => setHelpOpen(false)} aria-label="Close help">×</button>
          <p className="eyebrow">
            {ccaasExperience ? 'The communications car' : trainExperience ? 'Your co-pilot station' : 'A word with the portrait'}
          </p>
          <h2>Speak naturally.</h2>
          <p>
            {ccaasExperience
              ? 'Chuck is listening. Ask naturally about a contact-center problem, architecture, operations, or the software offering. CC controls captions, Mute closes the microphone, and End erases this conversation.'
              : trainExperience
                ? 'Iver is listening from the pilot chair and shares the live route conditions with you. Speak naturally and pause for an answer; you may interrupt at any time. CC controls captions, Mute closes the microphone, and End erases this journey immediately.'
                : `${activeProfile.name} is listening. Choose the other portrait by its name plaque, speak naturally, and pause for an answer. You may interrupt while a portrait is speaking. CC controls captions, Mute closes the microphone, and End erases this visit immediately.`}
          </p>
          {trainExperience && (
            <p className="route-footage-credit">
              The Northstar route visuals were generated locally for this prototype. The current five-second motion proof loops while the longer journey is being built.
            </p>
          )}
        </aside>
      )}

      {phase === 'error' && (
        <div className="session-veil" role="alert">
          <p className="eyebrow">
            {ccaasExperience ? 'The switchboard dropped a line' : trainExperience ? 'The speaking tube crackled' : 'The connection faltered'}
          </p>
          <h2>
            {ccaasExperience ? 'Chuck cannot hear you.' : trainExperience ? 'Iver cannot hear you.' : `${activeProfile.name} has gone still.`}
          </h2>
          <p>{errorMessage}</p>
          <button
            className="wake-button"
            type="button"
            onClick={() => void enterPortrait(
              ccaasExperience ? 'chuck' : trainExperience ? 'robot' : activeCharacter,
            )}
          >
            Try again <i aria-hidden="true">✦</i>
          </button>
        </div>
      )}

      {phase === 'ended' && (
        <div className="session-veil ended-veil">
          <p className="eyebrow">
            {ccaasExperience ? 'The switchboard is quiet' : trainExperience ? 'Northstar at rest' : 'The frame is quiet'}
          </p>
          <h2>{ccaasExperience ? 'Conversation complete.' : trainExperience ? 'Journey complete.' : 'Until next time.'}</h2>
          <p>Your microphone is off and this {trainExperience ? 'journey' : 'conversation'} has been erased.</p>
          <button
            className="wake-button"
            type="button"
            onClick={() => void enterPortrait(
              ccaasExperience ? 'chuck' : trainExperience ? 'robot' : activeCharacter,
            )}
          >
            {ccaasExperience ? 'Open the line again' : trainExperience ? 'Ride again' : 'Return to the gallery'} <i aria-hidden="true">✦</i>
          </button>
        </div>
      )}
    </main>
  );
}
